import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

type Slide = {
  imageUrl: string;
  headline?: string;
  caption?: string;
  ctaLabel?: string;
  ctaHref?: string;
};

interface HeroSliderProps {
  slides: Slide[];
  brandLogo?: string;
  brandTagline?: string;

  heightClass?: string;
  fullBleed?: boolean;
  intervalMs?: number;

  /** Eski prop’lar (geriye dönük). Vermezsen CSS var kullanır. */
  textPrimary?: string;
  accent?: string;
  accentHover?: string;
}

function cn(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(" ");
}

const HeroSlider: React.FC<HeroSliderProps> = ({
  slides,
  brandLogo,
  brandTagline,
  heightClass = "h-[44vh] sm:h-[52vh] md:h-[60vh] lg:h-[66vh]",
  fullBleed = false,
  intervalMs = 9000,
  textPrimary,
  accent,
  accentHover,
}) => {
  const [idx, setIdx] = useState(0);

  const timerRef = useRef<number | null>(null);
  const pausedRef = useRef(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const safeSlides = useMemo(() => (Array.isArray(slides) ? slides.filter(s => !!s?.imageUrl) : []), [slides]);
  const len = safeSlides.length;

  const stop = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const start = () => {
    if (len <= 1) return;
    stop();
    timerRef.current = window.setInterval(() => {
      setIdx((i) => (i + 1) % len);
    }, intervalMs);
  };

  const pause = () => {
    pausedRef.current = true;
    stop();
  };

  const resume = () => {
    pausedRef.current = false;
    start();
  };

  useEffect(() => {
    // slides değişince index taşmasın
    setIdx((i) => (len === 0 ? 0 : Math.min(i, len - 1)));
    start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, len]);

  useEffect(() => {
    const onVis = () => {
      if (document.hidden) stop();
      else if (!pausedRef.current) start();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [len, intervalMs]);

  const goTo = (i: number) => {
    if (!len) return;
    setIdx((i + len) % len);
    if (!pausedRef.current) start();
  };
  const prev = () => goTo(idx - 1);
  const next = () => goTo(idx + 1);

  if (!len) return null;

  const containerClasses = fullBleed ? "" : "max-w-7xl mx-auto px-4 sm:px-6";

  // CSS var fallback + geriye dönük props
  const styleVars: React.CSSProperties = {
    ["--hs-text" as any]: textPrimary || "rgb(var(--text))",
    ["--hs-primary" as any]: accent || "rgb(var(--primary))",
    ["--hs-primary-hover" as any]: accentHover || "rgb(var(--primary-hover))",
  };

  const active = safeSlides[idx];

  return (
    <div className={containerClasses}>
      <div
        ref={wrapRef}
        className={cn(
          "relative overflow-hidden select-none",
          fullBleed ? "rounded-none" : "rounded-3xl border shadow-sm",
          "bg-black"
        )}
        style={{
          ...styleVars,
          borderColor: fullBleed ? undefined : "rgb(var(--border))",
        }}
        onMouseEnter={pause}
        onMouseLeave={resume}
        onTouchStart={pause}
        onTouchEnd={resume}
      >
        {/* SLIDES */}
        <div className={cn("relative", heightClass)}>
          <div
            className="h-full w-full whitespace-nowrap transition-transform duration-700 ease-out will-change-transform"
            style={{ transform: `translateX(-${idx * 100}%)` }}
          >
            {safeSlides.map((s, i) => (
              <div key={i} className="inline-block w-full h-full align-top relative">
                <img
                  src={s.imageUrl}
                  alt={s.headline ?? ""}
                  className="w-full h-full object-cover"
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  draggable={false}
                />

                {/* hafif noise / film */}
                <div className="absolute inset-0 opacity-[0.10] pointer-events-none bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.18)_0%,transparent_55%)]" />
              </div>
            ))}
          </div>

          {/* OVERLAY (daha premium) */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.25)_0%,transparent_55%)]" />
          </div>

          {/* CONTENT */}
          <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6">
            <div className="max-w-3xl mx-auto text-center">
              {/* glass panel */}
              <div className="inline-block w-full rounded-3xl border border-white/10 bg-white/10 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.25)] px-4 sm:px-6 py-4 sm:py-5">
                {brandLogo && (
                  <img
                    src={brandLogo}
                    alt="logo"
                    className="h-8 sm:h-9 md:h-10 mx-auto mb-2 object-contain"
                    loading="eager"
                  />
                )}

                {brandTagline && (
                  <div className="text-xs sm:text-sm text-white/80 mb-1">
                    {brandTagline}
                  </div>
                )}

                <h1
                  className="text-lg sm:text-3xl md:text-4xl font-normal  leading-tight tracking-tight text-white drop-shadow"
                >
                  {active?.headline ?? ""}
                </h1>

                {active?.caption && (
                  <p className="mt-2 text-[13px] sm:text-base md:text-lg text-white/85 leading-relaxed max-w-2xl mx-auto">
                    {active.caption}
                  </p>
                )}

                {active?.ctaLabel && (
                  <div className="mt-4 flex items-center justify-center">
                    <a
                      href={active.ctaHref || "#"}
                      className={cn(
                        "inline-flex items-center justify-center",
                        "px-5 py-2.5 rounded-2xl font-semibold",
                        "text-white shadow-sm transition",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60",
                        "active:scale-[0.99]"
                      )}
                      style={{ background: "var(--hs-primary)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--hs-primary-hover)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "var(--hs-primary)")}
                    >
                      {active.ctaLabel}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* DOTS */}
          {len > 1 && (
            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
              {safeSlides.map((_, i) => {
                const isActive = i === idx;
                return (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    className={cn(
                      "h-2.5 rounded-full transition-all",
                      isActive ? "w-7 opacity-100" : "w-2.5 opacity-60"
                    )}
                    style={{
                      background: isActive ? "var(--hs-primary)" : "rgba(255,255,255,0.65)",
                    }}
                    aria-label={`slide ${i + 1}`}
                  />
                );
              })}
            </div>
          )}

          {/* ARROWS */}
          {len > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="Önceki"
                className={cn(
                  "absolute top-1/2 left-3 -translate-y-1/2",
                  "w-10 h-10 sm:w-11 sm:h-11 rounded-full",
                  " bg-gradient-to-r from-blue-600 to-teal-500 backdrop-blur-md",
                  "grid place-items-center text-white",
                  "shadow-md transition",
                  "hover:bg-white/15 active:scale-[0.98]",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                )}
              >
                <ChevronLeft size={22} />
              </button>

              <button
                onClick={next}
                aria-label="Sonraki"
                className={cn(
                  "absolute top-1/2 right-3 -translate-y-1/2",
                  "w-10 h-10 sm:w-11 sm:h-11 rounded-full",
                  " bg-gradient-to-r from-blue-600 to-teal-500 backdrop-blur-md",
                  "grid place-items-center text-white",
                  "shadow-md transition",
                  "hover:bg-white/15 active:scale-[0.98]",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                )}
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroSlider;
